import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, Receipt, Award, MapPin } from "lucide-react";
import { COMPANY, SERVICES } from "../data/site";
import ServiceCard from "../components/ServiceCard";
import ProcessSection from "../components/ProcessSection";
import TrustSection from "../components/TrustSection";
import ReviewsSection from "../components/ReviewsSection";
import ReviewsBanner from "../components/ReviewsBanner";
import BlogTeaser from "../components/BlogTeaser";
import ContactCTA from "../components/ContactCTA";
import FAQ from "../components/FAQ";
import { HOME_FAQS } from "../data/faqs";
import SEO from "../components/SEO";

const TRUST_POINTS = [
  { icon: Receipt, label: "Kostnadsfri bedömning" },
  { icon: ShieldCheck, label: "Tydlig offert med RUT" },
  { icon: Award, label: "Utbildade och försäkrade" },
  { icon: MapPin, label: "Hjälp även i svåra lägen" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Trädfällning i Laholm | Trädfällarna Laholm AB"
        description="Trädfällarna Laholm hjälper privatpersoner, företag och fastigheter med trädfällning, beskärning, stubbfräsning, bortforsling och markarbeten i Laholm med omnejd."
        path="/"
      />
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[560px] md:min-h-[620px] lg:min-h-[660px] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="/images/hero-home.webp"
            alt="Trädfällarna Laholm i arbete"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{ objectPosition: "60% 25%" }}
          />
          <div className="absolute inset-0 tf-hero-overlay" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-5">
              <span className="w-2 h-2 rounded-full bg-[#D95D2E]" />
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/90">
                Lokalt företag i Laholm
              </span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.05] tracking-tight tf-hero-text-shadow"
            >
              Trygg trädfällning i{" "}
              <span className="text-[#F2A57C]">Laholm</span> med omnejd
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-5 text-base sm:text-lg text-[#E8EAE5] leading-relaxed max-w-2xl tf-hero-text-shadow"
            >
              Vi hjälper privatpersoner, företag och fastighetsägare med säker
              trädfällning, trädvård, stubbfräsning och bortforsling. Alltid med
              tydlig offert, rätt utrustning och fokus på ett tryggt utfört arbete.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/kontakt#offert"
                data-testid="hero-cta-quote"
                className="inline-flex items-center justify-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors text-base shadow-lg shadow-black/20"
              >
                Få offert <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneE164}`}
                data-testid="hero-cta-phone"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/85 hover:bg-white hover:text-[#1B2A1E] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors text-base backdrop-blur-sm"
              >
                <Phone className="w-4 h-4" /> Ring {COMPANY.phone}
              </a>
            </div>

            <ul
              data-testid="hero-trust-points"
              className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-2.5 max-w-3xl"
            >
              {TRUST_POINTS.map(({ icon: Icon, label }, i) => (
                <li
                  key={label}
                  data-testid={`hero-trust-${i + 1}`}
                  className="flex items-center gap-2.5 bg-[#0F1A12]/65 border border-white/25 backdrop-blur-md rounded-lg px-3 py-2.5 shadow-md shadow-black/20"
                >
                  <Icon className="w-4 h-4 text-[#F2A57C] shrink-0" />
                  <span className="text-sm font-semibold text-white leading-tight">
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <ReviewsBanner />

      {/* SERVICES */}
      <section
        id="tjanster"
        data-testid="services-section"
        className="section-pad bg-[#F7F5F0] scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
            <div className="max-w-2xl">
              <span className="eyebrow">Tjänster</span>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1B2A1E] leading-tight tracking-tight">
                Allt runt träd, mark och tomt — utfört av lokala arborister
              </h2>
            </div>
            <p className="text-[#3a4338] text-lg max-w-md">
              Vi hjälper både privatpersoner, fastighetsägare och företag. Behöver du
              en kombination av tjänster löser vi det i samma uppdrag.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {SERVICES.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      <ProcessSection />
      <TrustSection />

      <ReviewsSection />

      <BlogTeaser />

      <FAQ
        id="home-faq"
        title="Vanliga frågor om trädfällning och trädvård"
        intro="Här samlar vi de frågor vi oftast får före en offert. Hittar du inte svaret du letar efter? Hör av dig — vi svarar gärna direkt."
        items={HOME_FAQS}
      />

      <ContactCTA />
    </div>
  );
}
